import React from 'react';
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  Grid,
  styledComponent,
  InformationItem,
  Badge,
  PluginCard,
  SvgIcon,
  Assets,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';
import useAsync from 'react-use/esm/useAsync';
import {
  Validate,
  useUserEntity,
  useProductEntity,
} from '@dewise-devex/backstage-plugin-utilities-common-client';
import { useUserProfile } from '@backstage/plugin-user-settings';
import { useApi } from '@backstage/core-plugin-api';
import { commonProductApiRef } from '@dewise-devex/backstage-plugin-customer-experience';

const Container = styledComponent('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  columnGap: '8px',
  alignItems: 'center',
  flexWrap: 'wrap',
  marginTop: '8px',

  div: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
}));

const Image = styledComponent<Record<string, string>>('img')(({ theme }) => ({
  height: 30,
  width: 30,
  objectFit: 'contain',
  borderRadius: '50%',
  border: `2px solid ${theme.palette.surface.onSurface}`,
  marginLeft: -9,
  boxShadow: `0 2px 4px rgba(0, 0, 0, 0.3)`,
  '&:first-of-type': {
    marginLeft: 0,
  },
}));

const ProductTitle = styledComponent('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  columnGap: '6px',
  color: theme.palette.primary.main,
  '& svg': {
    fill: theme.palette.primary.main,
  },
  marginBottom: '8px',
}));

export const Products: React.FC = () => {
  const userEntityService = useUserEntity();
  const productEntityCatalog = useProductEntity();
  const { profile } = useUserProfile();
  const CuXProductsApi = useApi(commonProductApiRef);

  const { value = [], loading } = useAsync(async () => {
    if (profile?.email) {
      const userEntity = await userEntityService.byEmail(profile.email);

      if (userEntity) {
        const productTeams = await productEntityCatalog.byEntity(userEntity, {
          withMembers: true,
        });

        return await Promise.all(
          productTeams.map(async product => {
            return {
              product,
              achievements: formatProductAchievements(
                await CuXProductsApi.getAchievements(product),
              ),
            };
          }),
        );
      }

      return [];
    }
    return [];
  }, [profile]);

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <PluginCard
      title="My Products"
      subheader="A quick glance on the products you are a part of."
    >
      {value.map(({ product, achievements }) => {
        const members = product?.members ?? [];
        const displayMembers = members.slice(0, 4);
        const remainingCount = members.length - 4;

        const displayAchievements = achievements.slice(0, 4);
        const remainingAchievements = achievements.length - 4;

        return (
          <Card key={product.metadata.name} sx={{ mb: 1 }} variant="basic">
            <CardHeader
              title={
                <div
                  style={{ display: 'inline-flex', flexDirection: 'column' }}
                >
                  <ProductTitle>
                    <SvgIcon.CodeDeployed />
                    <Typography
                      variant="titleSM"
                      sx={{ fontWeight: '500!important' }}
                    >
                      {product.metadata.title}
                    </Typography>
                  </ProductTitle>
                  <Typography variant="bodySM">
                    {product.metadata.description}
                  </Typography>
                </div>
              }
              color="basic"
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="labelSM">Members</Typography>
                  <Container>
                    <div>
                      {displayMembers.map(user => (
                        <Image
                          key={String(user.metadata.uid)}
                          src={String(user.spec.profile?.picture)}
                          alt={String(user.spec.profile?.displayName)}
                        />
                      ))}
                    </div>
                    {remainingCount > 0 && (
                      <Typography variant="bodySM">
                        {`+${remainingCount}`}
                      </Typography>
                    )}
                  </Container>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  {!Validate.isEmptyArray(achievements) && (
                    <>
                      <Typography variant="labelSM">Achievements</Typography>
                      <Container>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            columnGap: 8,
                          }}
                        >
                          {displayAchievements.map(badge => (
                            <Badge
                              key={badge.id}
                              name={badge.primary}
                              img={badge.image}
                              description={badge.secondary ?? ''}
                              dateAwarded={badge.metadata?.dateAwarded ?? ''}
                              version={badge.metadata?.version ?? ''}
                              size="small"
                            />
                          ))}
                        </div>
                        {remainingAchievements > 0 && (
                          <Typography variant="bodySM">
                            {`+${remainingAchievements}`}
                          </Typography>
                        )}
                      </Container>
                    </>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}
    </PluginCard>
  );
};

const badgeImages: { [key: string]: string } = {
  'Effective Collaboration': Assets.badgeEffectiveCollaboration,
  'Quality Code': Assets.badgeQualityCode,
  'Wise and Adaptive': Assets.badgeWiseAndAdaptive,
};

export function formatProductAchievements(
  achievements: Record<string, any>[],
): InformationItem[] {
  return achievements.map(achievement => ({
    id: achievement.id,
    primary: achievement.display_name,
    image: badgeImages[achievement.display_name] ?? undefined,
    secondary: achievement.description,
    metadata: {
      version: achievement.version,
      dateAwarded: achievement.start_date,
    },
  }));
}
